/* Tool-call chip + its document/page references — extracted from TeacherChat's */
/* inline block so the cours chat shows the same curriculum tool calls (chips) */
/* and BO document refs. Clicking a reference opens the document drawer. */

import { useEffect, useState } from 'react';
import { CaretRight, FileText } from '@phosphor-icons/react';
import ToolChip from './tool-chip';
import { ensureDocumentPdf } from '../lib/docs';
import { levelLabel, toolLabel, type ToolCall } from '../lib/editor-chat';
import { cn } from '@/lib/utils';

export type OpenDoc = (docId: string, page: number) => void;

export default function ToolBlock({ tool, live, onOpenDoc }: { tool: ToolCall; live?: boolean; onOpenDoc: OpenDoc }) {
  const allRefs = (tool.refs ?? []).filter(
    (r, i, a) => a.findIndex((x) => x.document_id === r.document_id && x.page === r.page) === i,
  );
  /* Group refs by document_id → one button per source with all its referenced pages. */
  const docGroups = Object.values(
    allRefs.reduce((acc, r) => {
      if (!acc[r.document_id]) acc[r.document_id] = { document_id: r.document_id, title: r.title, pages: [] as number[] };
      acc[r.document_id]!.pages.push(r.page);
      return acc;
    }, {} as Record<string, { document_id: string; title?: string | null; pages: number[] }>),
  ).map((g) => ({ ...g, pages: [...new Set(g.pages)].sort((a, b) => a - b) }));

  /* Lazy pre-warm: as soon as a doc is referenced, best-effort ensure its real */
  /* PDF is stored so the drawer opens on the PDF instantly (fire-and-forget). */
  useEffect(() => {
    docGroups.forEach(({ document_id }) => { void ensureDocumentPdf(document_id); });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [docGroups.map((g) => g.document_id).join(',')]);

  return (
    <div className="flex flex-col gap-[5px]">
      <ToolChip label={toolLabel(tool)} live={live} docCount={docGroups.length > 0 && !live ? docGroups.length : undefined} />
      {tool.levels && tool.levels.length > 0 && !live && (
        <div className="flex flex-wrap gap-1 pl-1">
          {tool.levels.map((lv, li) => (
            <span key={`${levelLabel(lv)}-${li}`} className="text-[10.5px] font-bold text-naia-green-deep bg-naia-green-bg rounded-full py-0.5 px-[7px] border border-border tracking-[0.02em]">
              {levelLabel(lv)}
            </span>
          ))}
        </div>
      )}
      {docGroups.length > 0 && (
        <div className="flex flex-col gap-[5px] pl-1">
          {docGroups.map((g) => (
            <div key={g.document_id} className="flex items-center gap-1 flex-wrap max-w-[340px]">
              {g.pages.length === 1 ? (
                <button
                  type="button"
                  onClick={() => onOpenDoc(g.document_id, g.pages[0] ?? 1)}
                  title={g.title ? `${g.title} — p. ${g.pages[0]}` : `p. ${g.pages[0]}`}
                  className="inline-flex items-center gap-[5px] min-h-11 max-w-[300px] text-[12px] font-semibold text-foreground bg-card border border-border rounded-sm px-[10px] py-[5px] cursor-pointer font-[inherit] shadow-card text-left"
                >
                  <FileText size={14} weight="regular" className="flex-none" />
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0">{g.title ?? 'Document'}</span>
                  <span className="flex-none text-[10.5px] font-bold text-muted-foreground bg-naia-card-alt rounded-full py-px px-[6px] border border-border">p.{g.pages[0]}</span>
                </button>
              ) : (
                <>
                  <span className="inline-flex items-center gap-[5px] max-w-[230px] text-[12px] font-semibold text-foreground bg-card border border-border rounded-sm px-[10px] py-[5px] shadow-card">
                    <FileText size={14} weight="regular" className="flex-none" />
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">{g.title ?? 'Document'}</span>
                  </span>
                  {g.pages.map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => onOpenDoc(g.document_id, page)}
                      title={`${g.title ?? 'Document'} — p. ${page}`}
                      className="flex-none text-[11px] font-bold text-naia-blue bg-naia-info-bg border border-border rounded-full py-[3px] px-2 cursor-pointer font-[inherit] min-h-11 min-w-[44px]"
                    >
                      p.{page}
                    </button>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Seuil mesure le 30/08 sur staging : 235 tours portent des appels d'outil,
   mediane 2, p75 7, p90 10, max 16. Grouper des 2 degraderait le cas le plus
   frequent ; a partir de 5 on replie un tour sur trois sans toucher la mediane. */
const SEUIL_GROUPEMENT = 5;

function resume(tools: ToolCall[]): string {
  const docs = new Set(tools.flatMap((t) => (t.refs ?? []).map((r) => r.document_id)));
  const etapes = `${tools.length} étapes`;
  if (docs.size === 0) return `Naïa a travaillé en ${etapes}`;
  return `Naïa a consulté ${docs.size} document${docs.size > 1 ? 's' : ''} en ${etapes}`;
}

/**
 * Liste d'appels d'outils d'un meme tour. Au-dela de SEUIL_GROUPEMENT elle se
 * replie derriere un resume : c'est le meme contenu, pas un contenu different.
 * Reste depliee tant qu'un appel est en cours — on ne cache pas ce qui bouge.
 */
export function ToolBlockList({
  tools,
  liveFor,
  onOpenDoc,
  keyPrefix = 't',
}: {
  tools: ToolCall[];
  liveFor?: (tool: ToolCall, index: number) => boolean;
  onOpenDoc: OpenDoc;
  keyPrefix?: string;
}) {
  const [deplie, setDeplie] = useState(false);
  const enCours = tools.some((t, i) => liveFor?.(t, i) ?? false);
  const blocs = tools.map((t, i) => (
    <ToolBlock key={`${keyPrefix}${i}`} tool={t} live={liveFor?.(t, i)} onOpenDoc={onOpenDoc} />
  ));

  if (tools.length < SEUIL_GROUPEMENT || enCours) return <>{blocs}</>;

  return (
    <div className="flex flex-col gap-[5px]">
      <button
        type="button"
        onClick={() => setDeplie((v) => !v)}
        aria-expanded={deplie}
        className="self-start inline-flex items-center gap-[6px] min-h-11 text-[12.5px] font-bold text-muted-foreground bg-secondary border border-border rounded-full px-[11px] py-[5px] cursor-pointer font-[inherit]"
      >
        <CaretRight size={12} weight="bold" className={cn('flex-none transition-transform', deplie && 'rotate-90')} />
        {resume(tools)}
      </button>
      {deplie && <div className="flex flex-col gap-[5px] pl-1">{blocs}</div>}
    </div>
  );
}
